import type { GatshipSearchOperator } from '@gat-ship/types'
import { ref, computed, type Ref, type ComputedRef } from 'vue'

/*
| **Operator** | **Name**     | **Semantics**                            |
| ------------ | ------------ | ---------------------------------------- |
| `:`          | Default      | Type-dependent (Equals, Contains, etc.). |
| `:=`         | Equals       | Exact match.                             |
| `:~`         | Like         | Pattern match (starts-with/wildcard).    |
| `:*`         | Contains     | Word-based token match.                  |
| `:@`         | FullText     | Semantic/Vector search.                  |
| `:>`         | Greater Than | Numeric/Date only.                       |
| `:>=`        | GTE          | Greater than or Equal.                   |
| `:<`         | Less Than    | Numeric/Date only.                       |
| `:<=`        | LTE          | Less than or Equal.                      |
*/

export interface GatshipSearchOperatorMetadata {
  /** The name of the operator. */
  name: string
  /** The semantics of the operator. */
  semantics: string
  /** The value of the operator (without the colon prefix). */
  operatorValue: string
  /** The human readable value of the operator. Used in the UI. */
  humanReadableTitle: string
  /** The human readable description of the operator. Used in the UI. */
  humanReadableDescription: string
}

export const gatshipSearchOperatorsMetadata: Record<
  GatshipSearchOperator,
  GatshipSearchOperatorMetadata
> = {
  equals: {
    name: 'Equals',
    operatorValue: '=',
    semantics: 'Exact match.',
    humanReadableTitle: 'Equals',
    humanReadableDescription: 'The field must exactly match the search term.'
  },
  contains: {
    name: 'Contains',
    operatorValue: '*',
    semantics: 'Word-based token match.',
    humanReadableTitle: 'Contains',
    humanReadableDescription: 'The value contains the search term.'
  },
  like: {
    name: 'Like',
    operatorValue: '~',
    semantics: 'Pattern match (starts-with/wildcard).',
    humanReadableTitle: 'Starts with',
    humanReadableDescription: 'The field must start with the search term.'
  }
} as const

/**
 * Get the value of a search operator.
 *
 * @param operator - The operator to get the value for.
 * @returns The value of the operator (without the colon prefix).
 */
export function getSearchOperatorValue(
  operator: GatshipSearchOperator
): string {
  return gatshipSearchOperatorsMetadata[operator].operatorValue
}

export type SearchFieldModelValue = {
  /** The name of the field property. */
  fieldName: string
  /** The value of the field property. */
  fieldValue: string | undefined
  /** The operator of the field property. */
  operator: GatshipSearchOperator | undefined
}

export function defineFilter(filter: {
  fieldName: SearchFieldModelValue['fieldName']
  defaultOperator: GatshipSearchOperator
  defaultFilterValue?: string
  allowedOperators?: Partial<Record<GatshipSearchOperator, boolean>>
}): {
  filterValue: Ref<string | undefined>
  operator: Ref<GatshipSearchOperator>
  fieldName: SearchFieldModelValue['fieldName']
  allowedOperators: ComputedRef<Partial<Record<GatshipSearchOperator, boolean>>>
} {
  const filterValue = ref<string | undefined>(filter.defaultFilterValue ?? '')

  const operator = ref<GatshipSearchOperator>(filter.defaultOperator)

  const allowedOperators = computed(() => {
    return (
      filter.allowedOperators ?? {
        [filter.defaultOperator]: true
      }
    )
  })

  return {
    filterValue,
    operator,
    fieldName: filter.fieldName,
    allowedOperators
  }
}

export function defineSearchFilters<
  T extends Record<string, ReturnType<typeof defineFilter>>
>(filters: T): T {
  return filters
}
